import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Building2, FileText, FlaskConical, Search, Loader2,
  MapPin as MapPinIcon, ShieldAlert, AlertTriangle, Coins, BarChart3, Wallet,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  ComposedChart, Line, RadialBarChart, RadialBar,
} from 'recharts';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import NavCluster from '../components/NavCluster';

// MODON brand palette
const PALETTE = {
  navy: '#1a4d80',
  navyDark: '#0f3457',
  navyLight: '#2c6aa8',
  gold: '#c9a14a',
  goldDark: '#a68431',
  goldLight: '#e9c87b',
  teal: '#0b8a7a',
  slate: '#475569',
  coral: '#d94a4a',
  amber: '#e4a83a',
  emerald: '#16a34a',
};
const CHART_COLORS = [PALETTE.navy, PALETTE.gold, PALETTE.teal, PALETTE.navyLight, PALETTE.amber, PALETTE.coral, PALETTE.goldDark, PALETTE.navyDark];
const RISK_COLORS = { high: '#c0392b', medium: PALETTE.gold, low: PALETTE.teal };
const RISK_LABELS = { high: 'عالي', medium: 'متوسط', low: 'منخفض' };

function MonthName(s, lang = 'ar') {
  const ar = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
  const en = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (!s) return '';
  const m = parseInt(String(s).split('-')[1], 10);
  if (Number.isNaN(m)) return s;
  return lang === 'en' ? (en[m - 1] || s) : (ar[m - 1] || s);
}

// Small KPI tile in the left column — matches the "Total Spending / Total Income" style of the reference.
function KpiTile({ icon: Icon, label, value, color, testid }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'ar-SA';
  return (
    <Card className="bg-white rounded-2xl shadow-[0_8px_22px_-10px_rgba(15,55,100,0.25)] border border-slate-200/70 p-4 flex items-center gap-3" data-testid={testid}>
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-[inset_0_-3px_0_rgba(0,0,0,0.2),0_6px_14px_-3px_rgba(15,55,100,0.4)]"
        style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
      >
        <Icon size={26} className="drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)]"/>
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="text-3xl font-extrabold leading-none tracking-tight" style={{ color: PALETTE.navyDark }}>
          {Number(value || 0).toLocaleString(locale)}
        </div>
        <div className="text-xs text-slate-500 font-bold tracking-wide">{label}</div>
      </div>
    </Card>
  );
}

// Gauge tile (top-left of the reference)
function GaugeTile({ value, label, max = 100, testid }) {
  const pct = Math.min(100, Math.max(0, value));
  const data = [{ name: 'val', value: pct, fill: PALETTE.navy }];
  return (
    <Card className="bg-white rounded-2xl shadow-[0_8px_22px_-10px_rgba(15,55,100,0.25)] border border-slate-200/70 p-3 flex flex-col items-center" data-testid={testid}>
      <div className="text-xs font-bold text-slate-600 mb-1">{label}</div>
      <div className="relative w-full" style={{ height: 120 }}>
        <ResponsiveContainer>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={180} endAngle={0}>
            <RadialBar dataKey="value" cornerRadius={12} fill={PALETTE.navy} background={{ fill: '#e2e8f0' }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-end justify-center pb-2">
          <div className="text-2xl font-extrabold" style={{ color: PALETTE.navyDark }}>{pct}%</div>
        </div>
      </div>
    </Card>
  );
}

// Generic chart container
function PanelCard({ title, children, testid, height = 280 }) {
  return (
    <Card className="bg-white rounded-2xl shadow-[0_10px_28px_-15px_rgba(15,55,100,0.22)] border border-slate-200/70 p-3 pt-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 right-3 left-3 h-[3px] rounded-full" style={{ background: `linear-gradient(90deg, ${PALETTE.gold}00 0%, ${PALETTE.gold} 50%, ${PALETTE.gold}00 100%)` }}/>
      <h3 className="text-center font-bold text-[13px] mb-2" style={{ color: PALETTE.navyDark }}>{title}</h3>
      <div style={{ width: '100%', height }}>{children}</div>
    </Card>
  );
}

// Donut with a centered icon + big total value (matches Purchases / Accepted Campaign visuals)
function DonutWithCenter({ data, total, icon: Icon, color = PALETTE.navy }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'ar-SA';
  return (
    <div className="relative w-full h-full">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={56}
            outerRadius={92}
            paddingAngle={2}
            label={({ percent }) => percent ? `${Math.round(percent*100)}%` : ''}
            labelLine={{ stroke: '#94a3b8' }}
          >
            {data.map((e, i) => <Cell key={i} fill={e.color}/>)}
          </Pie>
          <Tooltip/>
          <Legend wrapperStyle={{ fontSize: 11 }} layout="vertical" align="right" verticalAlign="middle" iconType="circle"/>
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" style={{ paddingRight: '20%' }}>
        {Icon && <Icon size={20} className="mb-0.5" style={{ color }} />}
        <div className="text-2xl font-extrabold leading-none" style={{ color: PALETTE.navyDark }}>
          {Number(total || 0).toLocaleString(locale)}
        </div>
      </div>
    </div>
  );
}

export default function PublicDashboard() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterCity, setFilterCity] = useState('all');
  const [filterActivity, setFilterActivity] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');

  // Search
  const [q, setQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/public/stats`),
          axios.get(`${API}/public/top-risk?limit=10`),
        ]);
        setStats(s.data);
        setTop(t.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const search = async () => {
    setSearching(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filterCity && filterCity !== 'all') params.city = filterCity;
      if (filterActivity && filterActivity !== 'all') params.activity = filterActivity;
      if (filterRisk && filterRisk !== 'all') params.risk = filterRisk;
      const r = await axios.get(`${API}/public/search`, { params });
      setSearchResults(r.data || []);
    } finally { setSearching(false); }
  };

  // Derived datasets
  const riskDonut = useMemo(() => stats ? [
    { name: t('publicDashboard.riskLow'), value: stats.lowRiskFactories || 0, color: PALETTE.teal },
    { name: t('publicDashboard.riskMedium'), value: stats.mediumRiskFactories || 0, color: PALETTE.gold },
    { name: t('publicDashboard.riskHigh'), value: stats.highRiskFactories || 0, color: PALETTE.coral },
  ] : [], [stats, t]);

  const activityDonut = useMemo(() => (stats?.activities || []).slice(0, 5).map((a, i) => ({
    name: a.name, value: a.count, color: CHART_COLORS[i % CHART_COLORS.length],
  })), [stats]);

  // Combined chart — monthly reports (bars) + cumulative chemicals (line) over last months
  const combinedSeries = useMemo(() => {
    const monthly = stats?.monthlySDS || [];
    let cumChems = 0;
    const chemicalsPerReport = stats?.totalSDS ? (stats.totalChemicals || 0) / stats.totalSDS : 0;
    return monthly.map((m) => {
      const reports = m.count || 0;
      cumChems += reports * chemicalsPerReport;
      return { name: MonthName(m.month, i18n.language), reports, chemicals: Math.round(cumChems) };
    });
  }, [stats, i18n.language]);

  // Hazard bar
  const hazardBar = useMemo(() => {
    if (!stats?.hazardCounts) return [];
    const enLabels = { flammable: 'Flammable', toxic: 'Toxic', corrosive: 'Corrosive', oxidizer: 'Oxidizer', explosive: 'Explosive', environment: 'Environmental', 'health-hazard': 'Health' };
    const arLabels = { flammable: 'اشتعال', toxic: 'سام', corrosive: 'أكّال', oxidizer: 'مؤكسد', explosive: 'متفجر', environment: 'بيئي', 'health-hazard': 'صحي' };
    const labels = i18n.language === 'en' ? enLabels : arLabels;
    const order = [
      { key: 'flammable', color: PALETTE.coral },
      { key: 'toxic', color: PALETTE.navyDark },
      { key: 'corrosive', color: PALETTE.goldDark },
      { key: 'oxidizer', color: PALETTE.gold },
      { key: 'explosive', color: PALETTE.coral },
      { key: 'environment', color: PALETTE.teal },
      { key: 'health-hazard', color: PALETTE.navy },
    ];
    return order.map((o) => ({ name: labels[o.key], value: stats.hazardCounts[o.key] || 0, color: o.color }));
  }, [stats, i18n.language]);

  // Gauge: percentage of factories with at least one SDS
  const sdsCoverage = useMemo(() => {
    if (!stats) return 0;
    const f = stats.totalFactories || 0;
    const fwSds = stats.factoriesWithSds ?? (stats.totalSDS ? Math.min(f, stats.totalSDS) : 0);
    return f ? Math.round((fwSds / f) * 100) : 0;
  }, [stats]);

  // Cities / activities for filter dropdowns
  const cityOptions = useMemo(() => (stats?.cities || []).map((c) => c.name), [stats]);
  const activityOptions = useMemo(() => (stats?.activities || []).map((a) => a.name), [stats]);

  return (
    <div className="modon-app-shell flex flex-col" dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
      <main className="flex-1 max-w-[1500px] w-full mx-auto px-3 md:px-5 py-5">
        {/* Outer dashboard frame — mimics the blue border in the reference */}
        <div
          className="rounded-2xl p-3 md:p-4 shadow-[0_18px_50px_-20px_rgba(15,55,100,0.4)]"
          style={{
            background: `linear-gradient(135deg, ${PALETTE.navy}, ${PALETTE.navyLight})`,
            border: `4px solid ${PALETTE.navy}`,
          }}
        >
          {/* Inner light surface */}
          <div className="modon-card rounded-xl p-3 md:p-4">
            {/* Top navigation: Back + Home */}
            <div className="flex items-center mb-3">
              <NavCluster homePath="/admin" testidPrefix="dashboard-top" />
            </div>
            {/* Title bar */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/70 p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <ModonLogo size={64} />
                <div className="min-w-0 text-center md:text-right">
                  <h1 className="text-xl md:text-2xl font-extrabold tracking-tight leading-tight" style={{ color: PALETTE.navyDark }}>
                    {t('publicDashboard.title')}
                  </h1>
                  <p className="text-[11px] text-slate-500 font-semibold mt-1 text-center">
                    {t('publicDashboard.subtitle')}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 w-full md:w-auto">
                <div>
                  <div className="text-[10px] font-bold text-slate-500 mb-0.5">{t('publicDashboard.filterCity')}</div>
                  <Select value={filterCity} onValueChange={setFilterCity}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-city"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 mb-0.5">{t('publicDashboard.filterActivity')}</div>
                  <Select value={filterActivity} onValueChange={setFilterActivity}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-activity"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      {activityOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 mb-0.5">{t('publicDashboard.filterRisk')}</div>
                  <Select value={filterRisk} onValueChange={setFilterRisk}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-risk"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value="high">{t('publicDashboard.riskHigh')}</SelectItem>
                      <SelectItem value="medium">{t('publicDashboard.riskMedium')}</SelectItem>
                      <SelectItem value="low">{t('publicDashboard.riskLow')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-slate-600 py-12 justify-center"><Loader2 className="animate-spin" size={18}/> جارٍ التحميل...</div>
            ) : !stats ? (
              <div className="text-slate-500 text-center py-12">تعذر تحميل الإحصائيات</div>
            ) : (
              <>
                {/* MAIN GRID — 3 columns: Left KPIs | Center charts | Right donuts */}
                <div className="grid grid-cols-12 gap-3">
                  {/* LEFT COLUMN — KPIs stack */}
                  <div className="col-span-12 lg:col-span-3 flex flex-col gap-3">
                    <GaugeTile value={sdsCoverage} label={t('publicDashboard.gauge')} testid="gauge-coverage" />
                    <KpiTile
                      icon={Building2}
                      label={t('publicDashboard.kpi.totalFactories')}
                      value={stats.totalFactories}
                      color={PALETTE.navy}
                      testid="kpi-factories"
                    />
                    <KpiTile
                      icon={FileText}
                      label={t('publicDashboard.kpi.totalSDS')}
                      value={stats.totalSDS}
                      color={PALETTE.gold}
                      testid="kpi-reports"
                    />
                    <KpiTile
                      icon={FlaskConical}
                      label={t('publicDashboard.kpi.totalChemicals')}
                      value={stats.totalChemicals}
                      color={PALETTE.teal}
                      testid="kpi-chemicals"
                    />
                    <KpiTile
                      icon={AlertTriangle}
                      label={t('publicDashboard.kpi.highRiskFactories')}
                      value={stats.highRiskFactories}
                      color={PALETTE.coral}
                      testid="kpi-high-risk"
                    />
                  </div>

                  {/* CENTER COLUMN — Combined + Bar */}
                  <div className="col-span-12 lg:col-span-6 flex flex-col gap-3">
                    <PanelCard title={t('publicDashboard.charts.combined')} testid="panel-combined" height={240}>
                      <ResponsiveContainer>
                        <ComposedChart data={combinedSeries} margin={{ top: 10, right: 10, bottom: 20, left: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }}/>
                          <YAxis yAxisId="left" tick={{ fontSize: 11, fill: PALETTE.teal }}/>
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: PALETTE.navy }}/>
                          <Tooltip/>
                          <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
                          <Bar yAxisId="left" dataKey="reports" name={t('publicDashboard.charts.reports')} fill={PALETTE.teal} radius={[6,6,0,0]} barSize={28}>
                            <LabelList dataKey="reports" position="top" fontSize={10} fill={PALETTE.teal}/>
                          </Bar>
                          <Line yAxisId="right" type="monotone" dataKey="chemicals" name={t('publicDashboard.charts.cumChems')} stroke={PALETTE.navy} strokeWidth={3} dot={{ r: 4, fill: PALETTE.navy }}/>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </PanelCard>

                    <PanelCard title={t('publicDashboard.charts.hazard')} testid="panel-hazard" height={240}>
                      <ResponsiveContainer>
                        <BarChart data={hazardBar} margin={{ top: 25, right: 5, bottom: 30, left: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} angle={-15} textAnchor="end" height={50} interval={0}/>
                          <YAxis hide/>
                          <Tooltip cursor={{ fill: 'rgba(26,77,128,0.05)' }}/>
                          <Bar dataKey="value" radius={[6,6,0,0]} barSize={32}>
                            {hazardBar.map((e, i) => <Cell key={i} fill={e.color}/>)}
                            <LabelList dataKey="value" position="top" fill="#0f3457" fontSize={12} fontWeight="bold"/>
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </PanelCard>
                  </div>

                  {/* RIGHT COLUMN — 2 donuts */}
                  <div className="col-span-12 lg:col-span-3 flex flex-col gap-3">
                    <PanelCard title={t('publicDashboard.charts.riskDonut')} testid="panel-risk-donut" height={240}>
                      <DonutWithCenter
                        data={riskDonut}
                        total={(stats.totalFactories || 0)}
                        icon={ShieldAlert}
                        color={PALETTE.coral}
                      />
                    </PanelCard>
                    <PanelCard title={t('publicDashboard.charts.activityDonut')} testid="panel-activity-donut" height={240}>
                      <DonutWithCenter
                        data={activityDonut}
                        total={stats.totalSDS || 0}
                        icon={BarChart3}
                        color={PALETTE.gold}
                      />
                    </PanelCard>
                  </div>
                </div>

                {/* Top 10 high risk strip */}
                <Card className="p-3 rounded-xl border border-slate-200/70 shadow-[0_8px_22px_-12px_rgba(15,55,100,0.2)] mt-3 bg-white">
                  <h3 className="font-bold mb-2 flex items-center gap-2 text-sm" style={{ color: PALETTE.navyDark }}>
                    <ShieldAlert size={16} className="text-red-600"/> {t('publicDashboard.topTitle')}
                  </h3>
                  {top.length === 0 ? (
                    <div className="text-slate-500 text-xs py-3 text-center">لا توجد بيانات متاحة</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" dir="rtl">
                        <thead className="text-[11px]" style={{ background: `${PALETTE.navy}10`, color: PALETTE.navyDark }}>
                          <tr>
                            <th className="text-right p-2">#</th>
                            <th className="text-right p-2">{t('publicDashboard.topFactory')}</th>
                            <th className="text-right p-2">{t('publicDashboard.topActivity')}</th>
                            <th className="text-right p-2">{t('publicDashboard.topCity')}</th>
                            <th className="text-right p-2">{t('publicDashboard.topRisk')}</th>
                            <th className="text-right p-2">{t('publicDashboard.topChemicals')}</th>
                            <th className="text-right p-2">{t('publicDashboard.topUpdated')}</th>
                            <th className="text-right p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {top.map((row, i) => (
                            <tr key={row.user_id} className="border-t hover:bg-[#1a4d80]/[0.04]" data-testid={`top-risk-row-${i}`}>
                              <td className="p-2 font-bold">{i + 1}</td>
                              <td className="p-2 font-bold text-slate-900">{row.factoryName}</td>
                              <td className="p-2 text-slate-700">{row.activity}</td>
                              <td className="p-2 text-slate-700">{row.city}</td>
                              <td className="p-2">
                                <Badge style={{ backgroundColor: RISK_COLORS[row.level] || '#64748b', color: 'white' }}>
                                  {t('publicDashboard.risk' + (row.level || 'low').charAt(0).toUpperCase() + (row.level || 'low').slice(1))} ({row.score})
                                </Badge>
                              </td>
                              <td className="p-2 text-slate-700">{row.chemicalsCount}</td>
                              <td className="p-2 text-slate-500 text-[11px]">{row.updated_at ? new Date(row.updated_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ar-SA') : '—'}</td>
                              <td className="p-2">
                                <Link to={`/sds/view/${row.sds_id}`}>
                                  <Button size="sm" variant="outline" className="text-[11px] h-7">{t('publicDashboard.viewSDS')}</Button>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* Search bar */}
                <Card className="p-3 rounded-xl border border-slate-200/70 shadow-[0_8px_22px_-12px_rgba(15,55,100,0.2)] mt-3 bg-white">
                  <h3 className="font-bold mb-2 flex items-center gap-1 text-sm" style={{ color: PALETTE.navyDark }}><Search size={14}/> {t('publicDashboard.searchTitle')}</h3>
                  <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
                    <Input
                      placeholder={t('publicDashboard.searchPlaceholder')}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && search()}
                      className="flex-1"
                      data-testid="public-search-q"
                    />
                    <Button onClick={search} disabled={searching} style={{ background: PALETTE.navy }} className="text-white hover:opacity-90 shadow-md" data-testid="public-search-btn">
                      {searching ? <Loader2 size={14} className="animate-spin mx-1"/> : <Search size={14} className="mx-1"/>} {t('common.search')}
                    </Button>
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {searchResults.map((r) => (
                        <Card key={r.id} className="p-2" data-testid={`search-result-${r.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-bold text-slate-900 text-xs truncate">{r.factoryName}</div>
                              <div className="text-[11px] text-slate-500 truncate">{r.activity} • {r.factoryCity}</div>
                              <div className="text-[11px] text-slate-600 mt-0.5">{r.chemicalsCount} مادة</div>
                            </div>
                            <Badge style={{ backgroundColor: RISK_COLORS[r.risk?.level] || '#64748b', color: 'white' }} className="shrink-0 text-[10px]">
                              {r.risk?.labelAr || RISK_LABELS[r.risk?.level]}
                            </Badge>
                          </div>
                          <Link to={`/sds/view/${r.id}`} className="block mt-1.5">
                            <Button size="sm" variant="outline" className="w-full text-[11px] h-7">{t('publicDashboard.viewSDS')}</Button>
                          </Link>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Footer mini actions */}
                <div className="flex justify-between items-center mt-3 px-1 text-xs text-white/90">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400"/> {t('publicDashboard.liveUpdate')} • {new Date().toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ar-SA')}
                  </span>
                  <div className="flex gap-2">
                    <Link to="/map"><Button size="sm" variant="secondary" className="h-7 text-xs"><MapPinIcon size={12} className="mx-1"/> {t('publicDashboard.mapBtn')}</Button></Link>
                    <Button size="sm" onClick={() => window.history.back()} className="h-7 text-xs bg-white text-[#1a4d80] hover:bg-slate-100" data-testid="dashboard-back-btn">{t('publicDashboard.backBtn')}</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter/>
    </div>
  );
}
